import { useState } from 'react';
import { X, Check, Mail, Shield } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const ConsentModal = ({ isOpen, onClose, onSubmit, locale = 'it' }) => {
  const [email, setEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [updatesOptIn, setUpdatesOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isIT = locale === 'it';

  const labels = {
    title: isIT ? 'Salva la tua bozza' : 'Save Your Draft',
    subtitle: isIT 
      ? 'Inserisci la tua email per ricevere un riepilogo e poter riprendere la conversazione in qualsiasi momento.'
      : 'Enter your email to receive a summary and resume the conversation anytime.',
    emailLabel: isIT ? 'La tua email' : 'Your Email',
    emailPlaceholder: isIT ? 'nome@esempio.com' : 'name@example.com',
    privacyLabel: isIT 
      ? 'Accetto la Privacy Policy e il trattamento dei dati personali'
      : 'I accept the Privacy Policy and data processing',
    updatesLabel: isIT 
      ? 'Desidero ricevere aggiornamenti sul mio viaggio via email'
      : 'I want to receive trip updates via email',
    saveBtn: isIT ? 'Salva e continua' : 'Save and Continue',
    skipBtn: isIT ? 'Continua senza salvare' : 'Continue without saving',
    privacyLink: isIT ? 'Leggi la Privacy Policy' : 'Read Privacy Policy',
    requiredError: isIT ? 'Email e consenso privacy sono obbligatori' : 'Email and privacy consent are required',
    invalidEmail: isIT ? 'Inserisci un\'email valida' : 'Enter a valid email',
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    setError('');
    
    if (!email || !privacyAccepted) {
      setError(labels.requiredError);
      return;
    }
    
    if (!validateEmail(email)) {
      setError(labels.invalidEmail);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ email, privacyAccepted, updatesOptIn });
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving consent');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="consent-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-slide-up">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="consent-close-btn"
        >
          <X size={20} />
        </button>
        
        {/* Header */}
        <div className="p-6 pb-4 text-center">
          <div className="w-12 h-12 bg-umbria-olive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="text-umbria-olive" size={24} />
          </div>
          <h2 className="text-xl font-serif font-semibold text-umbria-charcoal">
            {labels.title}
          </h2>
          <p className="text-sm text-gray-500 mt-2">
            {labels.subtitle}
          </p>
        </div>
        
        {/* Form */}
        <div className="px-6 pb-6 space-y-5">
          {/* Email input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-umbria-charcoal">
              {labels.emailLabel}
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={labels.emailPlaceholder}
              className="rounded-xl border-umbria-stone focus:border-umbria-olive focus:ring-umbria-olive/20"
              data-testid="consent-email-input"
            />
          </div>
          
          {/* Privacy checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="privacy"
              checked={privacyAccepted}
              onCheckedChange={setPrivacyAccepted}
              className="mt-0.5 border-umbria-stone data-[state=checked]:bg-umbria-olive data-[state=checked]:border-umbria-olive"
              data-testid="consent-privacy-checkbox"
            />
            <div className="space-y-1">
              <Label 
                htmlFor="privacy" 
                className="text-sm text-umbria-charcoal cursor-pointer leading-tight"
              >
                {labels.privacyLabel} *
              </Label>
              <a 
                href="/privacy" 
                target="_blank" 
                className="text-xs text-umbria-olive hover:underline block"
              >
                {labels.privacyLink}
              </a>
            </div>
          </div>
          
          {/* Updates opt-in */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="updates"
              checked={updatesOptIn}
              onCheckedChange={setUpdatesOptIn}
              className="mt-0.5 border-umbria-stone data-[state=checked]:bg-umbria-olive data-[state=checked]:border-umbria-olive"
              data-testid="consent-updates-checkbox"
            />
            <Label 
              htmlFor="updates" 
              className="text-sm text-umbria-charcoal cursor-pointer leading-tight"
            >
              {labels.updatesLabel}
            </Label>
          </div>
          
          {/* Error message */}
          {error && (
            <p className="text-sm text-red-500 text-center" data-testid="consent-error">
              {error}
            </p>
          )}
          
          {/* Buttons */}
          <div className="space-y-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="consent-save-btn"
            >
              {isSubmitting ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Check size={18} />
                  {labels.saveBtn}
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="btn-ghost w-full text-gray-500"
              data-testid="consent-skip-btn"
            >
              {labels.skipBtn}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;
